import { Injectable } from '@angular/core';
import { Participant } from '../interfaces/participant';

export interface AnonymizedParticipant {
  // Fields that may be anonymized
  enisNumber: string;
  firstName: string;
  lastName: string;
  center: { id?: string; name: string } | null;

  // All other fields remain as-is but may be masked
  [key: string]: any;
}

export interface AnonymizationContext {
  userRole: string; // 'OPERATOR', 'CENTER', etc.
  userCenterName: string | null; // User's center name
  chainData: Array<{ recipient: Participant | null; donor: Participant | null; alternative: boolean }>;
  isUserInChain: boolean; // Whether the user's center has a participant in this chain
  userParticipantIndices: number[]; // Indices of pairs where user's center participant appears
}

@Injectable({
  providedIn: 'root'
})
export class AnonymizationService {
  private readonly MASK = '******';

  constructor() {}

  /**
   * Anonymize a participant based on the user's role, center, and position in the chain.
   *
   * Rules for CENTER role:
   * 1. Full info for: my own participants (donor/recipient from my center)
   * 2. Full info for: donors giving kidney to my recipient
   * 3. Name + Center for: recipients receiving kidney from my donor (mask ENIS and medical data)
   * 4. Center only for: rest of chain (if different center than mine) - mask names, ENIS and medical data
   * 5. Complete anonymization (******) for: participants in other chains (chains without my center's participants)
   */
  anonymizeParticipant(
    participant: Participant | null,
    participantType: 'recipient' | 'donor',
    pairIndex: number,
    context: AnonymizationContext
  ): AnonymizedParticipant | null {
    // If participant is null (not found in allParticipants)
    if (!participant) {
      // For non-CENTER roles, return null (will be handled in template as missing data)
      // For CENTER roles, create a masked placeholder
      if (context.userRole !== 'CENTER') {
        return null;
      }
      return this.createPlaceholderParticipant();
    }

    // Non-CENTER roles see everything
    if (context.userRole !== 'CENTER') {
      return this.createFullParticipant(participant);
    }

    // CENTER role logic
    if (!context.isUserInChain) {
      // Rule 4: Other chains - completely anonymized
      return this.createCompletelyAnonymized(participant);
    }

    // User is in this chain - apply granular rules
    return this.applyChainBasedAnonymization(participant, participantType, pairIndex, context);
  }

  /**
   * Create a placeholder for participants that couldn't be found
   */
  private createPlaceholderParticipant(): AnonymizedParticipant {
    return {
      enisNumber: this.MASK,
      firstName: this.MASK,
      lastName: this.MASK,
      center: { name: this.MASK },
      abo: this.MASK,
      hla: this.MASK,
      unacceptableHla: this.MASK,
      pra: this.MASK
    } as AnonymizedParticipant;
  }

  /**
   * Apply anonymization rules for participants within the user's chain
   */
  private applyChainBasedAnonymization(
    participant: Participant | null,
    participantType: 'recipient' | 'donor',
    pairIndex: number,
    context: AnonymizationContext
  ): AnonymizedParticipant | null {
    if (!participant) {
      return null;
    }

    const isUserCenter = participant.center?.name === context.userCenterName;
    const isUserPair = context.userParticipantIndices.includes(pairIndex);

    // Rule 1: My recipient or their matched donor - full info
    if (isUserPair && isUserCenter) {
      return this.createFullParticipant(participant);
    }

    // Check if this is a donor giving to my recipient
    const isDonorForMyRecipient = this.isDonorForUserRecipient(pairIndex, participantType, context);

    if (isDonorForMyRecipient) {
      // Donor giving to my recipient - show full info
      return this.createFullParticipant(participant);
    }

    // Check if this is a recipient receiving from my donor
    const isRecipientOfMyDonor = this.isRecipientOfUserDonor(pairIndex, participantType, context);

    if (isRecipientOfMyDonor) {
      // Recipient receiving from my donor - show name and center (mask medical data)
      return this.createNameCenterOnlyMaskMedical(participant);
    }

    // Rule 3: Rest of chain
    if (isUserCenter) {
      // Same center - show full info
      return this.createFullParticipant(participant);
    } else {
      // Different center - center only (mask names, ENIS and medical data)
      return this.createNameAndCenterOnly(participant);
    }
  }

  /**
   * Check if this is a donor giving a kidney to the user's recipient
   */
  private isDonorForUserRecipient(
    pairIndex: number,
    participantType: 'recipient' | 'donor',
    context: AnonymizationContext
  ): boolean {
    // Only relevant for donors
    if (participantType !== 'donor') {
      return false;
    }

    // Check if this donor is in the same pair as user's recipient
    for (const userIndex of context.userParticipantIndices) {
      const userPair = context.chainData[userIndex];
      if (!userPair) continue;

      const hasUserRecipient = userPair.recipient?.center?.name === context.userCenterName;

      if (hasUserRecipient && pairIndex === userIndex) {
        // This donor is giving to my recipient
        return true;
      }
    }

    return false;
  }

  /**
   * Check if this is a recipient receiving a kidney from the user's donor
   */
  private isRecipientOfUserDonor(
    pairIndex: number,
    participantType: 'recipient' | 'donor',
    context: AnonymizationContext
  ): boolean {
    // Only relevant for recipients
    if (participantType !== 'recipient') {
      return false;
    }

    // Check if this recipient is in the SAME pair as user's donor
    for (const userIndex of context.userParticipantIndices) {
      const userPair = context.chainData[userIndex];
      if (!userPair) continue;

      const hasUserDonor = userPair.donor?.center?.name === context.userCenterName;

      if (hasUserDonor && pairIndex === userIndex) {
        // The recipient receiving from my donor is in the SAME pair
        // But only if the recipient is NOT from my center (already handled by Rule 1)
        const recipientCenter = context.chainData[pairIndex]?.recipient?.center?.name;
        if (recipientCenter !== context.userCenterName) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Create context for anonymization based on chain data and user info
   */
  createAnonymizationContext(
    userRole: string,
    userCenterName: string | null,
    chainData: Array<{ recipient: Participant | null; donor: Participant | null; alternative: boolean }>
  ): AnonymizationContext {
    const userParticipantIndices: number[] = [];
    let isUserInChain = false;

    // Find all pairs where user's center participant appears
    chainData.forEach((pair, index) => {
      const hasUserRecipient = pair.recipient?.center?.name === userCenterName;
      const hasUserDonor = pair.donor?.center?.name === userCenterName;

      if (hasUserRecipient || hasUserDonor) {
        isUserInChain = true;
        userParticipantIndices.push(index);
      }
    });

    return {
      userRole,
      userCenterName,
      chainData,
      isUserInChain,
      userParticipantIndices
    };
  }

  /**
   * Create full participant data (no anonymization)
   */
  private createFullParticipant(participant: Participant): AnonymizedParticipant {
    return { ...participant } as AnonymizedParticipant;
  }

  /**
   * Create participant with name and center but mask medical data
   * Shows: firstName, lastName, center
   * Masks: ENIS, all medical/sensitive data
   */
  private createNameCenterOnlyMaskMedical(participant: Participant): AnonymizedParticipant {
    return {
      ...participant,
      enisNumber: this.MASK, // Mask ENIS for partial anonymization
      firstName: participant.firstName,
      lastName: participant.lastName,
      center: participant.center,
      // Mask all medical/sensitive fields
      abo: this.MASK,
      hla: this.MASK,
      unacceptableHla: this.MASK,
      pra: this.MASK,
      dob: undefined,
      age: this.MASK
    } as AnonymizedParticipant;
  }

  /**
   * Create participant with center only (Rule 4 - rest of chain)
   * Shows: center only
   * Masks: ENIS, names, all medical/sensitive data
   */
  private createNameAndCenterOnly(participant: Participant): AnonymizedParticipant {
    return {
      ...participant,
      enisNumber: this.MASK, // Mask ENIS for partial anonymization
      firstName: this.MASK,
      lastName: this.MASK,
      center: participant.center,
      // Mask all medical/sensitive fields
      abo: this.MASK,
      hla: this.MASK,
      unacceptableHla: this.MASK,
      pra: this.MASK,
      dob: undefined,
      age: this.MASK
    } as AnonymizedParticipant;
  }

  /**
   * Create participant with center only (Rule 3)
   */
  private createCenterOnly(participant: Participant): AnonymizedParticipant {
    return {
      ...participant,
      enisNumber: participant.enisNumber, // Keep ENIS for technical matching
      firstName: this.MASK,
      lastName: this.MASK,
      center: participant.center,
      // Mask all medical/sensitive fields
      abo: this.MASK,
      hla: this.MASK,
      unacceptableHla: this.MASK,
      pra: this.MASK,
      dob: undefined,
      age: this.MASK
    } as AnonymizedParticipant;
  }

  /**
   * Create completely anonymized participant (Rule 4 - other chains)
   */
  private createCompletelyAnonymized(participant: Participant): AnonymizedParticipant {
    return {
      ...participant,
      enisNumber: this.MASK,
      firstName: this.MASK,
      lastName: this.MASK,
      center: { name: this.MASK },
      // Mask all medical/sensitive fields
      abo: this.MASK,
      hla: this.MASK,
      unacceptableHla: this.MASK,
      pra: this.MASK,
      dob: undefined,
      age: this.MASK
    } as AnonymizedParticipant;
  }
}
